// The dock (release 6.2, Kareem 4 Nov): the AI panel and the activity panel are not overlays on a wide screen. They
// dock on the right and push the page, which narrows with a smooth width transition (the sidebar never changes). No
// scrim: working on the page leaves them open; they close with their × or Escape (with focus inside). They stay open
// while you move between pages: the activity panel on its activity (?activity=<id>, linkable, kept across pages),
// the AI panel following the page unless its scope is pinned.
//
// Width: drag the left edge (380px to half the screen), remembered per person (default 460). Both open: side by
// side (activity, then AI) when the page keeps at least 720px, else one dock with Activity | AI tabs. Below 1100px
// they overlay as before; a phone gets full sheets.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import type { SessionUser } from '@dpw/shared';
import { usePhone } from '../lib/media';
import { scrim, sidePanel, spring } from '../lib/motion';
import { useSheetFocus } from '../ui/Sheet';
import { ActivityView } from '../pages/Activity';

export const DOCK_MIN = 380, DOCK_DEFAULT = 460, DOCK_MAIN_MIN = 720, DOCK_FROM = 1100;

interface DockApi {
  /** Docking is on (a wide screen): panels push the page instead of covering it. */
  wide: boolean;
  aiOpen: boolean; setAiOpen: (b: boolean) => void;
  /** Where the AI panel draws itself while docked. */
  aiSlot: HTMLElement | null; setAiSlot: (e: HTMLElement | null) => void;
  activityId: string | null; openActivity: (id: string) => void; closeActivity: () => void;
  width: number; setWidth: (w: number) => void;
  tab: 'activity' | 'ai'; setTab: (t: 'activity' | 'ai') => void;
}
const Ctx = createContext<DockApi | null>(null);
export const useDock = () => useContext(Ctx);

/** Open an activity in the activity panel (any list's rows). */
export function useActivityDrawer() {
  const d = useContext(Ctx);
  return { open: (id: string) => d?.openActivity(id), close: () => d?.closeActivity(), id: d?.activityId ?? null };
}

function useWide() {
  const q = `(min-width: ${DOCK_FROM}px)`;
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => { const mq = window.matchMedia(q); const f = () => setM(mq.matches); mq.addEventListener('change', f); return () => mq.removeEventListener('change', f); }, [q]);
  return m;
}

/**
 * The page narrows or widens as a panel docks or leaves, and its rows reflow. The row the activity came from stays
 * where it is on screen meanwhile (the scroll follows it until the width settles), so the list is where you left it.
 */
function holdRow(id: string) {
  const el = document.querySelector<HTMLElement>(`main.cmain [data-activity="${CSS.escape(id)}"]`);
  if (!el) return;
  // Its place in the page's layout, leaving out the rows' own layout animations (transforms), which would only wobble.
  const at = () => { let y = 0; for (let e: HTMLElement | null = el; e; e = e.offsetParent as HTMLElement | null) y += e.offsetTop; return y - window.scrollY; };
  const top = at();
  if (top < 0 || top > window.innerHeight) return;
  // Until nothing has moved for a moment (the width's transition, then the rows' own layout springs), at most 1.5s.
  const start = performance.now();
  let moved = start;
  const step = () => {
    if (!el.isConnected) return;
    const now = performance.now();
    const d = at() - top;
    if (Math.abs(d) >= 0.5) { window.scrollBy(0, d); moved = now; }
    if (now - moved < 300 && now - start < 1500) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

const clampW = (w: number) => Math.round(Math.max(DOCK_MIN, Math.min(w, Math.max(DOCK_MIN, window.innerWidth / 2))));

export function DockProvider({ me, children }: { me: SessionUser; children: ReactNode }) {
  const phone = usePhone();
  const wideQ = useWide();
  const wide = wideQ && !phone;
  const [aiOpen, setAiOpenRaw] = useState(false);
  // The AI panel closing hands the dock back to the activity.
  const setAiOpen = useCallback((b: boolean) => { setAiOpenRaw(b); if (!b) setTab('activity'); }, []);
  const [aiSlot, setAiSlot] = useState<HTMLElement | null>(null);
  const [tab, setTab] = useState<'activity' | 'ai'>('ai');
  const key = `dpw.dock.${me.id}`;
  const [width, setW] = useState(() => { try { const v = Number(localStorage.getItem(key)); return v ? clampW(v) : DOCK_DEFAULT; } catch { return DOCK_DEFAULT; } });
  const setWidth = useCallback((w: number) => {
    const v = clampW(w);
    setW(v);
    try { localStorage.setItem(key, String(v)); } catch { /* storage blocked: it still resizes for now */ }
  }, [key]);

  // The activity panel: ?activity=<id> on the address (linkable, survives a reload). It belongs to the page it was
  // opened on (Kareem, 5 Nov): going to another page closes it; the AI panel is the one that stays across pages.
  const [params] = useSearchParams();
  const loc = useLocation();
  const nav = useNavigate();
  const urlId = params.get('activity');
  const [activityId, setActivityId] = useState<string | null>(urlId);
  const closing = useRef(false);
  const activityRef = useRef(activityId);
  activityRef.current = activityId;
  const navType = useNavigationType();
  const lastPath = useRef(loc.pathname);
  useEffect(() => {
    const moved = loc.pathname !== lastPath.current;
    lastPath.current = loc.pathname;
    if (urlId && urlId !== activityId) { setActivityId(urlId); setTab('activity'); return; }
    // Back (or Forward) to an address without it: the panel closes, as it opened.
    if (!urlId && activityId && navType === 'POP') { setActivityId(null); setTab('ai'); closing.current = false; return; }
    // Another page: the panel closes with the page it was opened on.
    if (!urlId && activityId && moved) { setActivityId(null); setTab('ai'); closing.current = false; opener.current = null; return; }
    // The same page setting its own parameters dropped it: put it back (the panel stays on its activity).
    if (!urlId && activityId && !closing.current) {
      const p = new URLSearchParams(loc.search);
      p.set('activity', activityId);
      nav({ pathname: loc.pathname, search: `?${p}`, hash: loc.hash }, { replace: true, preventScrollReset: true });
    }
    closing.current = false;
  }, [urlId, loc.pathname, loc.search]);
  const setParam = useCallback((id: string | null) => {
    const p = new URLSearchParams(window.location.search);
    if (id) p.set('activity', id); else p.delete('activity');
    const s = p.toString();
    nav({ pathname: window.location.pathname, search: s ? `?${s}` : '', hash: window.location.hash }, { preventScrollReset: true });
  }, [nav]);
  // Closing gives the focus back to what opened it (the row), without scrolling the list.
  const opener = useRef<HTMLElement | null>(null);
  const openActivity = useCallback((id: string) => {
    const el = document.activeElement;
    if (el instanceof HTMLElement && !el.closest('.dslot, .actov')) opener.current = el;
    holdRow(id);
    setActivityId(id); setTab('activity'); setParam(id);
  }, [setParam]);
  const closeActivity = useCallback(() => {
    if (activityRef.current) holdRow(activityRef.current);
    closing.current = true; setActivityId(null); setTab('ai'); setParam(null);
    const el = opener.current;
    opener.current = null;
    window.setTimeout(() => { if (el?.isConnected) el.focus({ preventScroll: true }); }, 0);
  }, [setParam]);

  const value = useMemo<DockApi>(() => ({ wide, aiOpen, setAiOpen, aiSlot, setAiSlot, activityId, openActivity, closeActivity, width, setWidth, tab, setTab }),
    [wide, aiOpen, aiSlot, activityId, openActivity, closeActivity, width, setWidth, tab]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The dock itself, after the page in the desktop shell: the activity panel, then the AI panel's slot. */
export function DockArea({ me }: { me: SessionUser }) {
  const d = useContext(Ctx)!;
  const [vw, setVw] = useState(() => window.innerWidth);
  const [side, setSide] = useState(236);
  useEffect(() => {
    const f = () => { setVw(window.innerWidth); setSide(document.querySelector('aside.cside')?.getBoundingClientRect().width ?? 236); };
    f();
    window.addEventListener('resize', f);
    const ro = new ResizeObserver(f);
    const a = document.querySelector('aside.cside');
    if (a) ro.observe(a);
    return () => { window.removeEventListener('resize', f); ro.disconnect(); };
  }, []);
  const act = d.wide && !!d.activityId;
  const ai = d.wide && d.aiOpen;
  const both = act && ai;
  const sideBySide = both && vw - side - 2 * d.width >= DOCK_MAIN_MIN;
  const total = !act && !ai ? 0 : sideBySide ? 2 * d.width : d.width;
  const showAct = act && (!both || sideBySide || d.tab === 'activity');
  const showAi = ai && (!both || sideBySide || d.tab === 'ai');

  // Drag the left edge to resize (each panel is this wide; side by side, the dock is twice that).
  const drag = useRef<{ x: number; w: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const onDown = (e: React.PointerEvent) => { drag.current = { x: e.clientX, w: d.width }; setDragging(true); (e.target as HTMLElement).setPointerCapture(e.pointerId); };
  const onMove = (e: React.PointerEvent) => { const g = drag.current; if (g) d.setWidth(g.w + (g.x - e.clientX) / (sideBySide ? 2 : 1)); };
  const onUp = () => { drag.current = null; setDragging(false); };

  return (
    <div className={`dock${total ? ' on' : ''}${dragging ? ' dragging' : ''}`} style={{ width: total }} data-layout={both ? (sideBySide ? 'side' : 'tabs') : act ? 'activity' : ai ? 'ai' : 'none'}>
      {total > 0 && (
        <div className="dresize" role="separator" aria-orientation="vertical" aria-label="Resize the panel" aria-valuemin={DOCK_MIN} aria-valuenow={d.width} tabIndex={0}
          onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
          onKeyDown={e => { if (e.key === 'ArrowLeft') d.setWidth(d.width + 20); if (e.key === 'ArrowRight') d.setWidth(d.width - 20); }} />
      )}
      {both && !sideBySide && (
        <div className="dtabs" role="tablist" aria-label="Panels">
          <button type="button" role="tab" aria-selected={d.tab === 'activity'} className={d.tab === 'activity' ? 'on' : ''} onClick={() => d.setTab('activity')}>Activity</button>
          <button type="button" role="tab" aria-selected={d.tab === 'ai'} className={d.tab === 'ai' ? 'on' : ''} onClick={() => d.setTab('ai')}>AI</button>
        </div>
      )}
      <div className="dslots">
        {act && (
          <div className="dslot dact" style={{ width: d.width, display: showAct ? undefined : 'none' }}>
            <ActivityView key={d.activityId!} me={me} id={d.activityId!} panel onClose={d.closeActivity} />
          </div>
        )}
        <div className="dslot dai" ref={d.setAiSlot} style={{ width: d.width, display: showAi ? undefined : 'none' }} />
      </div>
    </div>
  );
}

/** Below 1100px (or on a phone) the activity panel overlays the page: a sheet from the right, or a full sheet. */
export function ActivityOverlay({ me }: { me: SessionUser }) {
  const d = useContext(Ctx);
  const phone = usePhone();
  const reduce = useReducedMotion();
  const box = useRef<HTMLDivElement>(null);
  const id = d && !d.wide ? d.activityId : null;
  return (
    <AnimatePresence>
      {id && d && <OverlayBody key={id} id={id} me={me} phone={phone} reduce={!!reduce} box={box} onClose={d.closeActivity} />}
    </AnimatePresence>
  );
}

function OverlayBody({ id, me, phone, reduce, box, onClose }: { id: string; me: SessionUser; phone: boolean; reduce: boolean; box: React.RefObject<HTMLDivElement | null>; onClose: () => void }) {
  useSheetFocus(box, onClose);
  return (
    <div className={phone ? 'ph actov-host' : 'actov-host'}>
      <motion.button type="button" className="scrim actov-scrim" aria-label="Close the activity" tabIndex={-1} onClick={onClose} variants={scrim} initial="initial" animate="animate" exit="exit" />
      <motion.div ref={box} className={`actov${phone ? ' phone' : ''}`} role="dialog" aria-modal="true" aria-label="Activity"
        initial={reduce ? { opacity: 0 } : phone ? { y: '100%' } : { x: '100%' }} animate={reduce ? { opacity: 1 } : phone ? { y: 0 } : { x: 0 }}
        exit={reduce ? { opacity: 0 } : phone ? { y: '100%' } : { x: '100%', transition: sidePanel.close }} transition={phone || reduce ? spring.gentle : sidePanel.open}>
        <ActivityView me={me} id={id} panel onClose={onClose} />
      </motion.div>
    </div>
  );
}
